<template>
  <div>
    <div>
      <template v-if="clientId?.includes('IDP')">
        <div class="text-xl text-gray-800">{{ lang.signInToUse }}</div>
        <div class="text-3xl text-red-400">{{ globalLang.titleIDP }}</div>
      </template>
      <template v-else-if="clientId?.includes('TMC')">
        <div class="text-xl text-gray-800">{{ lang.signIn.toUpperCase() }}<br />{{ lang.toStartUsing }}</div>
        <div class="text-3xl text-red-400">{{ globalLang.titleTMC }}</div>
      </template>
      <template v-else>
        <div class="text-xl text-gray-800">{{ lang.signInToYour }}</div>
        <div class="text-3xl text-red-400">{{ lang.tmAccount }}</div>
      </template>
    </div>

    <div class="flex flex-col my-4">
      <div class="text-sm text-gray-800 mb-2">
        {{ lang.emailOrPhone }}
      </div>
      <EmailInput
        id="account"
        v-model.trim="account"
        :isError="Boolean(errorMsg) || isAccountNotExist"
        :disabled="isAccountInputFixed"
        @enter="next"
        class="mb-1"
        autofocus
      />

      <div v-show="isAccountNotExist" class="mt-1 mb-4">
        <div class="text-red-600 text-sm">{{ lang.emailAccountIsNotExisted }}</div>
        <div v-show="allowSignUp" class="mt-2">
          <Link :to="{ name: 'auth/signUpByEmail' }" tc="SignUpWithEmail">{{
            lang.signUpWithThisEmail
          }}</Link>
        </div>
      </div>

      <template v-if="!isAccountNotExist">
        <ErrorMessage :errMsg="errorMsg" />
      </template>

      <Button
        id="btn-check-sign-in-account"
        type="submit"
        name="button"
        class="mb-3"
        color="primary"
        :label="lang.next"
        :loading="isLoading"
        :disabled="!account || !recaptcha"
        @click="next"
      />

      <div v-if="allowSignUp" class="text-sm text-gray-800 my-2" :class="{'text-lg': clientId?.includes('TMC')}">
        <span class="mr-1">{{ lang.noAccountYet }}</span>
        <Link :to="{ name: 'auth/signUp' }" tc="GoToCreateAccount" :customClass="{'text-lg': clientId?.includes('TMC')}">
          {{ lang.createNewAccount }}
        </Link>
      </div>
      <div class="mb-3">
        <Link :href="link.helpSignIn" tc="TroubleSignIn">
          {{ lang.troubleSignIn }}
        </Link>
      </div>
    </div>

    <div v-if="allowSocialAuth">
      <div class="relative border border-gray-400 border-r-0 border-l-0 border-b-0 mb-6">
        <div
          class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center px-1 bg-white text-gray-700 text-sm whitespace-nowrap"
        >
          {{ lang.continueWithOtherOptions }}
        </div>
      </div>

      <SocialLogin authType="sign_in" :google="supportSocialGoogle" :apple="supportSocialApple" />
    </div>

    <ActionDialog
      :value="isSocialLoginFail"
      :title="lang.signInUnsuccess"
      :message="provider === 'google' ? lang.googleAccountNotExised : lang.appleAccountNotExised"
      :buttonText="lang.next"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import { MAIL_REGEX, PHONE_REGEX } from '@/constant/regex.js';
import { hashData } from '@/utilities/index';

import ErrorMessage from '@/components/ErrorMessage.vue';
import ActionDialog from '@/components/ActionDialog.vue';
import Button from '@/components/Button.vue';
import Link from '@/components/Link.vue';
import EmailInput from '@/modules/auth/components/EmailInput.vue';
import SocialLogin from '@/modules/auth/components/SocialLogin.vue';

const ERRORCODE_NOT_ALLOW_SOCIAL_SIGNUP = 3001;

export default {
  components: {
    ErrorMessage,
    ActionDialog,
    Button,
    Link,
    EmailInput,
    SocialLogin,
  },
  data() {
    return {
      isLoading: false,
      errorMsg: '',
      account: '',
      provider: '',
      isAccountNotExist: false,
      isSocialLoginFail: false,
    };
  },
  async mounted() {
    // clear sign in info
    this.resetState();
    if (!this.allowSignIn) {
      this.$router.replace({ name: 'auth/signUp' });
      return;
    }

    const { email, provider, private: privateEmail, error_code } = this.$route.query;

    if (provider && error_code) {
      this.provider = provider;

      if (+error_code === ERRORCODE_NOT_ALLOW_SOCIAL_SIGNUP) {
        this.isSocialLoginFail = true;

        this.triggerEvent({
          eventName: 'SocialAccountNotExist',
          addInfo: {
            Method: provider,
          },
        });

        return;
      }
    }

    if (provider && email) {
      // Social login
      this.provider = provider;
      this.setEmailAccount(email);

      const maskEmail = privateEmail ? privateEmail === '1' : false;

      this.triggerEvent({
        eventName: 'GetProfileFromSocial',
        addInfo: {
          Method: provider,
          MaskEmail: maskEmail,
        },
      });

      this.$router.push({ name: 'auth/signInWithSocialAccount', query: this.$route.query });
      return;
    } else {
      if (this.prefilledAccount) this.account = this.prefilledAccount;
      this.triggerEvent({ eventName: 'PageView' });
    }

    if (this.loginFrom && this.loginFrom === 'activate_oot_activation') {
      this.sendAdobeData({
        friendlyPageName: 'Get SSS Trial welcome Sign in'
      })
    } else if (!this.allowSignUp) {
      this.sendAdobeData({
        friendlyPageName: 'Sign in only Home page'
      })
    }
  },
  computed: {
    ...mapState(['userLang', 'clientId', 'loginFrom', 'vendor']),
    ...mapState({
      globalLang: (state) => state.lang,
      globalErrorMsg: (state) => state.lang.error_msg,
    }),
    ...mapState('auth', [
      'recaptcha',
      'lang',
      'allowAction',
      'isAccountInputFixed',
      'prefilledAccount',
      'supportAuthMethod',
      'disableEmailVerification',
    ]),
    isCHTSkipEnterPhone() {
      if(this.vendor === "CHT" && this.account && this.recaptcha) {
        return true
      }
      return false
    },
    link() {
      return {
        helpSignIn: `https://gr.trendmicro.com/GREntry/NonPayment?Target=MyAccount&FunID=SignIn&Locale=${this.userLang}`,
      };
    },
    validationMsg() {
      return this.lang.validationRules;
    },
    allowSignUp() {
      return this.allowAction.includes('sign_up');
    },
    allowSignIn() {
      return this.allowAction.includes('sign_in');
    },
    allowSocialAuth() {
      if (this.supportAuthMethod.length > 0 && !this.supportAuthMethod.join().includes('social')) {
        return false;
      }

      return true;
    },
    supportSocialApple() {
      if (this.supportAuthMethod.length > 0 && !this.supportAuthMethod.includes('social-apple')) {
        return false;
      }

      return true;
    },
    supportSocialGoogle() {
      if (this.supportAuthMethod.length > 0 && !this.supportAuthMethod.includes('social-google')) {
        return false;
      }

      return true;
    },
  },
  watch: {
    // For cht flow skip user enter phone
    isCHTSkipEnterPhone(value) {
      if(value) {
        this.setIsLoading(true);
        this.next();
      }
    },
    account() {
      this.errorMsg = '';
      this.isAccountNotExist = false;
    },
  },
  methods: {
    ...mapMutations('auth', [
      'setSignInErrMsg',
      'setEmailAccount',
      'setPhoneNumberAccount',
      'setNationalNumber',
      'setSignInStep',
      'setIsPaidAccount',
      'setIsEmailVerified',
      'setIsLoading'
    ]),
    ...mapActions(['triggerEvent', 'sendAdobeData']),
    ...mapActions('auth', ['checkAccount']),
    async next() {
      if (!this.account || !this.recaptcha) return;

      this.errorMsg = '';
      this.isAccountNotExist = false;

      this.isLoading = true;
      const hexAccount = await hashData(this.account, 'SHA-256');
      const isPhoneAccount = PHONE_REGEX.test(this.account);

      if (isPhoneAccount) {
        this.triggerEvent({
          eventName: 'CheckAccount',
          addInfo: {
            current_method: 'phone',
            current_account: hexAccount,
          },
        });

        this.setPhoneNumberAccount(this.account);
        this.setNationalNumber(this.account);

        if (this.isAccountInputFixed) {
          this.setSignInStep(2);
        }

        this.$router.push({ name: 'auth/signInByPhone' });
      } else {
        const isEmailAccount = MAIL_REGEX.test(this.account);

        if (!isEmailAccount) {
          this.triggerEvent({
            eventName: 'CheckAccount',
            current_account: hexAccount,
          });

          this.errorMsg = this.validationMsg.CHECK_EMAIL;
          this.isLoading = false;
          return;
        } else {
          this.triggerEvent({
            eventName: 'CheckAccount',
            addInfo: {
              current_method: 'email',
              current_account: hexAccount,
            },
          });

          const requestBody = {
            email: this.account,
          };

          try {
            const res = await this.checkAccount(requestBody);
            this.setEmailAccount(this.account);
            this.isLoading = false;

            if (res.data.result === 'new') {
              // new account
              this.triggerEvent({ eventName: 'SignInWithNewEmailAccount' });
              if (!this.allowSignUp) {
                this.isAccountNotExist = true;
              } else {
                if (this.disableEmailVerification) {
                  this.$router.push({ name: 'auth/signUpByEmail' });
                } else {
                  this.$router.push({ name: 'auth/signUpVerifyEmail' });
                }
              }
            } else {
              // account existed
              const isPasswordSet = res.data.support_auth_method.includes('password');
              if (!isPasswordSet) {
                this.$router.push({ name: 'auth/signInAccountNoPassword' });
              } else {
                this.$router.push({ name: 'auth/signInByEmail' });
              }
            }
          } catch (e) {
            this.triggerEvent({ eventName: 'CheckAccountFail' });
            this.errorMsg = this.globalErrorMsg.common;
            this.isLoading = false;
            return;
          }
        }
      }
      this.setIsLoading(false);
    },
    closeDialog() {
      this.isSocialLoginFail = false;
      this.provider = '';
      this.triggerEvent({ eventName: 'CloseSocialLoginErrorDialog' });
    },
    resetState() {
      this.setSignInErrMsg('');
      this.setIsPaidAccount('0');
      this.setIsEmailVerified('0');
    },
  },
};
</script>
